import { createRef, useEffect, useMemo, useState } from 'react';
import { isEmpty, isNil } from 'lodash';
import { CheckboxGroup, TextCheckbox } from 'taboola-ultimate-ui';
import { FILTER_OPERATOR } from '../../../config/filters/filterOperator';
import styles from './openFilterUnit.module.scss';

const OptionRenderer = ({ label, value, isSelected, data, selectOption }) => (
    <div onClick={() => selectOption(data)} className={styles['multi-option']}>
        <CheckboxGroup selectedValues={isSelected ? [value] : []}>
            <TextCheckbox
                title={label}
                value={value}
                labelClassName={styles['option-color']}
                inputClassName={styles['text-checkbox']}
            />
        </CheckboxGroup>
    </div>
);

// We render null to allow values render in value container
const MultiValueRenderer = () => null;

const ValueContainerRenderer = ({ getValue, children }) => {
    const values = getValue();

    return (
        <div className={styles['value-container-wrapper']}>
            <div className={styles['value-container']}>{values.map(({ label }) => label).join(', ')}</div>

            {children}
        </div>
    );
};

const MULTI_SELECT_MAX_HEIGHT = 600;
const EMPTY_VALUES = [];

export const useFilterListValueState = ({
    type,
    initialFilter,
    isMultiSelectFilter,
    getValueOptions,
    operator,
    operatorOptions,
    availableFiltersMap,
}) => {
    const filterConfig = availableFiltersMap[type];
    const { values: initialValues = EMPTY_VALUES } = initialFilter || {};
    const valueRef = createRef();
    const [values, setValues] = useState(initialValues);
    const [valuePage, setValuePage] = useState(0);
    const [valuesOpen, setValuesOpen] = useState(false);
    const valueDropdownProps = useMemo(
        () =>
            isMultiSelectFilter(type)
                ? {
                      isMulti: true,
                      closeMenuOnSelect: false,
                      hideSelectedOptions: false,
                      optionRenderer: OptionRenderer,
                      multiValueRenderer: MultiValueRenderer,
                      valueContainerRenderer: ValueContainerRenderer,
                      clearable: false,
                      defaultValue: values,
                      blurInputOnSelect: false,
                      searchable: filterConfig?.searchable || false,
                      maxMenuHeight: MULTI_SELECT_MAX_HEIGHT,
                  }
                : {
                      selectedValueObject: values[0],
                      blurInputOnSelect: true,
                      searchable: true,
                  },
        [isMultiSelectFilter, values, type, filterConfig]
    );
    const [inputValue, setInputValue] = useState('');
    const openValuesDropdown = () => setValuesOpen(true);
    const closeValuesDropdown = () => setValuesOpen(false);
    const handleInputChange = newInputValue => {
        setInputValue(newInputValue);
        setValuePage(0);
    };
    const changeValues = newValue => {
        if (isNil(newValue)) {
            setValues(EMPTY_VALUES);
            return;
        }
        if (Array.isArray(newValue)) {
            setValues(newValue);
            return;
        }
        setValues([newValue]);
    };
    const handleValueChange = newValueObject => {
        changeValues(newValueObject);
        setInputValue('');
    };

    const handleGetOptions = searchInput => {
        const newPage = valuePage + 1;
        setValuePage(newPage);
        return getValueOptions(type, searchInput, newPage);
    };
    const showValuesDropdown =
        isEmpty(operatorOptions) ||
        operator === FILTER_OPERATOR.IN ||
        operator === FILTER_OPERATOR.NOT_IN ||
        filterConfig?.options;

    useEffect(() => {
        if (!showValuesDropdown) {
            setValues(EMPTY_VALUES);
        }
    }, [showValuesDropdown]);

    useEffect(() => {
        if (type && isEmpty(values)) {
            if (valueRef.current) {
                valueRef.current.select.focus();
            }
            openValuesDropdown();
        }
    }, [type, valueRef, values]);

    return {
        showValuesDropdown,
        isComplete: !showValuesDropdown || !isEmpty(values),
        values,
        setValues,
        setValuePage,
        ...valueDropdownProps,
        selectRef: ref => {
            valueRef.current = ref;
        },
        onFocus: openValuesDropdown,
        onBlur: closeValuesDropdown,
        inputValue,
        onInputChange: handleInputChange,
        getOptions: handleGetOptions,
        onChange: handleValueChange,
        menuIsOpen: valuesOpen,
    };
};
